import { useEffect, useCallback, useRef } from 'react';

interface TVNavigationOptions {
  /** Container element to scope navigation (defaults to document) */
  container?: HTMLElement | null;
  /** Callback when back/escape is pressed */
  onBack?: () => void;
  /** Callback when select/enter is pressed */
  onSelect?: (element: HTMLElement) => void;
  /** Enable wrap-around navigation */
  wrapAround?: boolean;
  /** Auto-focus first element on mount */
  autoFocus?: boolean;
}

/**
 * TV D-pad Navigation Hook
 *
 * Provides keyboard navigation for TV remotes using arrow keys.
 * Handles focus management, back button, and spatial navigation.
 *
 * Supports:
 * - Arrow keys for directional navigation
 * - Enter/Space for selection
 * - Backspace/Escape for back navigation
 * - Tab key support (for accessibility)
 */
export function useTVNavigation(options: TVNavigationOptions = {}) {
  const {
    container = null,
    onBack,
    onSelect,
    wrapAround = true,
    autoFocus = true
  } = options;

  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Get all focusable elements within container
  const getFocusableElements = useCallback((): HTMLElement[] => {
    const root = container || document;
    const selector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[role="button"]:not([disabled])'
    ].join(', ');

    return Array.from(root.querySelectorAll<HTMLElement>(selector))
      .filter(el => {
        // Filter out hidden elements
        const style = window.getComputedStyle(el);
        return style.display !== 'none' &&
               style.visibility !== 'hidden' &&
               el.offsetParent !== null;
      });
  }, [container]);

  // Get element position for spatial navigation
  const getElementCenter = (el: HTMLElement): { x: number; y: number } => {
    const rect = el.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  // Find best element in direction
  const findElementInDirection = useCallback((
    current: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const elements = getFocusableElements();
    const currentIndex = elements.indexOf(current);
    const currentPos = getElementCenter(current);

    // Simple linear navigation for left/right
    if (direction === 'left' || direction === 'right') {
      const delta = direction === 'right' ? 1 : -1;
      let nextIndex = currentIndex + delta;

      if (wrapAround) {
        nextIndex = (nextIndex + elements.length) % elements.length;
      }

      if (nextIndex >= 0 && nextIndex < elements.length) {
        return elements[nextIndex];
      }
      return null;
    }

    // Spatial navigation for up/down
    let bestMatch: HTMLElement | null = null;
    let bestScore = Infinity;

    elements.forEach(el => {
      if (el === current) return;

      const pos = getElementCenter(el);
      const dx = pos.x - currentPos.x;
      const dy = pos.y - currentPos.y;

      // Check if element is in the correct direction
      const isInDirection =
        (direction === 'up' && dy < -10) ||
        (direction === 'down' && dy > 10);

      if (!isInDirection) return;

      // Score based on distance and alignment
      // Prefer elements that are more aligned horizontally
      const distance = Math.sqrt(dx * dx + dy * dy);
      const alignment = Math.abs(dx) / (Math.abs(dy) + 1);
      const score = distance + alignment * 50;

      if (score < bestScore) {
        bestScore = score;
        bestMatch = el;
      }
    });

    // Fallback to linear navigation if no spatial match
    if (!bestMatch) {
      const delta = direction === 'down' ? 1 : -1;
      let nextIndex = currentIndex + delta;

      if (wrapAround) {
        nextIndex = (nextIndex + elements.length) % elements.length;
      }

      if (nextIndex >= 0 && nextIndex < elements.length) {
        return elements[nextIndex];
      }
    }

    return bestMatch;
  }, [getFocusableElements, wrapAround]);

  // Handle keydown events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const activeElement = document.activeElement as HTMLElement;
    const elements = getFocusableElements();

    // If nothing is focused, focus first element
    if (!elements.includes(activeElement)) {
      if (elements.length > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        elements[0].focus();
        return;
      }
    }

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        const upEl = findElementInDirection(activeElement, 'up');
        if (upEl) {
          upEl.focus();
          lastFocusedRef.current = upEl;
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        const downEl = findElementInDirection(activeElement, 'down');
        if (downEl) {
          downEl.focus();
          lastFocusedRef.current = downEl;
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        const leftEl = findElementInDirection(activeElement, 'left');
        if (leftEl) {
          leftEl.focus();
          lastFocusedRef.current = leftEl;
        }
        break;

      case 'ArrowRight':
        event.preventDefault();
        const rightEl = findElementInDirection(activeElement, 'right');
        if (rightEl) {
          rightEl.focus();
          lastFocusedRef.current = rightEl;
        }
        break;

      case 'Enter':
      case ' ':
        // Let default behavior handle buttons/links
        // But trigger callback if provided
        if (onSelect && activeElement) {
          onSelect(activeElement);
        }
        break;

      case 'Backspace':
      case 'Escape':
        event.preventDefault();
        if (onBack) {
          onBack();
        }
        // Dispatch custom event for components to handle
        window.dispatchEvent(new CustomEvent('tv-back'));
        break;

      default:
        break;
    }
  }, [getFocusableElements, findElementInDirection, onBack, onSelect]);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    // Auto-focus first element
    if (autoFocus) {
      const timer = setTimeout(() => {
        const elements = getFocusableElements();
        if (elements.length > 0 && !document.activeElement?.matches('[tabindex]')) {
          elements[0].focus();
          lastFocusedRef.current = elements[0];
        }
      }, 100);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, autoFocus, getFocusableElements]);

  // Restore focus after re-render
  const restoreFocus = useCallback(() => {
    if (lastFocusedRef.current && document.body.contains(lastFocusedRef.current)) {
      lastFocusedRef.current.focus();
    } else {
      const elements = getFocusableElements();
      if (elements.length > 0) {
        elements[0].focus();
        lastFocusedRef.current = elements[0];
      }
    }
  }, [getFocusableElements]);

  // Focus a specific element
  const focusElement = useCallback((element: HTMLElement) => {
    element.focus();
    lastFocusedRef.current = element;
  }, []);

  return {
    restoreFocus,
    focusElement,
    getFocusableElements
  };
}

export default useTVNavigation;
